import { hasAnyRole } from './session'
import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { failureKind } from './api'
import {
  fetchBoot,
  retryBoot,
  session,
  roleHomeRoute,
  unreachableText,
} from './session'
import { confirmDiscard, hasDirtyChanges } from './composables/useDirtyGuard'

const salonOps = ['Salon Owner', 'Salon Manager']
const frontDesk = ['Salon Owner', 'Salon Manager', 'Salon Receptionist']
const allStaff = [
  'Salon Staff',
  'Salon Receptionist',
  'Salon Owner',
  'Salon Manager',
]
const customer = ['Customer']
const platformAdmin = ['Glamora Platform Admin']

const routes = [
  // ── Root redirect ─────────────────────────────────────────────────────────
  // Handled in beforeEach: authenticated → role home, guest → /login
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/Login.vue'), // never rendered — guard redirects
    meta: { public: true },
  },

  // ── Public / Auth ─────────────────────────────────────────────────────────
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/Login.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('@/pages/Signup.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/signup/customer',
    name: 'SignupCustomer',
    component: () => import('@/pages/SignupCustomer.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/verify',
    name: 'VerifyEmail',
    component: () => import('@/pages/VerifyEmail.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/pages/ResetPassword.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/setup',
    name: 'SetupWizard',
    component: () => import('@/pages/SetupWizard.vue'),
    meta: { roles: ['Salon Owner'] },
  },

  // ── Salon ops ─────────────────────────────────────────────────────────────
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/pages/Dashboard.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/appointments',
    name: 'Appointments',
    component: () => import('@/pages/AppointmentsCalendar.vue'),
    meta: { roles: frontDesk, feature: 'appointments' },
  },
  {
    path: '/appointments/:name',
    name: 'AppointmentDetail',
    component: () => import('@/pages/AppointmentDetail.vue'),
    meta: { roles: frontDesk, feature: 'appointments' },
  },
  {
    path: '/front-desk',
    name: 'FrontDesk',
    component: () => import('@/pages/FrontDesk.vue'),
    meta: { roles: frontDesk, feature: 'appointments' },
  },
  {
    path: '/pos',
    name: 'POS',
    component: () => import('@/pages/POS.vue'),
    meta: { roles: frontDesk, feature: 'pos' },
  },
  {
    path: '/pos/receipt/:name',
    name: 'Receipt',
    component: () => import('@/pages/Receipt.vue'),
    meta: { roles: [...frontDesk, 'Salon Staff', ...customer] },
    // A customer opening a staff receipt link gets their own portal receipt view.
    beforeEnter: (to: RouteLocationNormalized) =>
      hasAnyRole(customer) && !hasAnyRole([...frontDesk, 'Salon Staff'])
        ? { path: `/portal/receipts/${encodeURIComponent(String(to.params.name))}` }
        : true,
  },
  {
    path: '/customers',
    name: 'Customers',
    component: () => import('@/pages/Customers.vue'),
    meta: { roles: frontDesk, feature: 'customers' },
  },
  {
    path: '/customers/:name',
    name: 'CustomerDetail',
    component: () => import('@/pages/CustomerDetail.vue'),
    meta: { roles: [...frontDesk, 'Salon Staff'], feature: 'customers' },
  },
  {
    path: '/staff',
    name: 'Staff',
    component: () => import('@/pages/Staff.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/staff/access',
    name: 'StaffAccess',
    component: () => import('@/pages/StaffAccess.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/leave',
    name: 'LeaveRequests',
    component: () => import('@/pages/LeaveRequests.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/commissions',
    name: 'Commissions',
    component: () => import('@/pages/Commissions.vue'),
    meta: { roles: frontDesk, feature: 'commissions' },
  },
  {
    path: '/commissions/rates',
    name: 'CommissionRates',
    component: () => import('@/pages/CommissionRates.vue'),
    meta: { roles: salonOps, feature: 'commissions' },
  },
  {
    path: '/staff/:name',
    name: 'StaffDetail',
    component: () => import('@/pages/StaffDetail.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/inventory',
    name: 'Inventory',
    component: () => import('@/pages/Inventory.vue'),
    meta: { roles: salonOps, feature: 'inventory' },
  },
  {
    path: '/inventory/stores',
    name: 'Stores',
    component: () => import('@/pages/Stores.vue'),
    meta: { roles: salonOps, feature: 'inventory' },
  },
  {
    // No purchase-order backend: purchases are recorded as supplier bills.
    path: '/inventory/po',
    name: 'PurchaseOrders',
    redirect: '/finance/bills',
  },
  {
    path: '/services',
    name: 'Services',
    component: () => import('@/pages/Services.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/services/prices',
    name: 'PriceList',
    component: () => import('@/pages/PriceList.vue'),
    meta: { roles: [...salonOps, 'Salon Receptionist'] },
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('@/pages/Products.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/shop/orders',
    name: 'ShopOrders',
    component: () => import('@/pages/ShopOrders.vue'),
    meta: { roles: frontDesk },
  },
  {
    path: '/branches',
    name: 'Branches',
    component: () => import('@/pages/Branches.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/campaigns',
    name: 'Campaigns',
    component: () => import('@/pages/Campaigns.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/finance',
    name: 'Finance',
    component: () => import('@/pages/Finance.vue'),
    meta: { roles: salonOps, feature: 'reports' },
  },
  {
    path: '/finance/invoices',
    name: 'FinanceInvoices',
    component: () => import('@/pages/FinanceInvoices.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/finance/expenses',
    name: 'Expenses',
    component: () => import('@/pages/Expenses.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/finance/bills',
    name: 'Bills',
    component: () => import('@/pages/Bills.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/finance/suppliers',
    name: 'Suppliers',
    component: () => import('@/pages/Suppliers.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/finance/cash-up',
    name: 'CashUp',
    component: () => import('@/pages/CashUp.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/finance/payouts',
    name: 'Payouts',
    component: () => import('@/pages/Payouts.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/finance/reports',
    name: 'FinanceReports',
    component: () => import('@/pages/FinanceReports.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/referrals',
    name: 'Referrals',
    component: () => import('@/pages/Referrals.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/referrals/statements/:name',
    name: 'ReferralStatement',
    component: () => import('@/pages/ReferralStatement.vue'),
    meta: { roles: salonOps },
  },
  {
    path: '/finance/mpesa',
    name: 'MpesaAdmin',
    component: () => import('@/pages/MpesaAdmin.vue'),
    meta: { roles: frontDesk, feature: 'finance' },
  },
  {
    path: '/reports/closeout',
    name: 'Closeout',
    component: () => import('@/pages/Closeout.vue'),
    meta: { roles: salonOps, feature: ['reports', 'finance'] },
  },
  {
    path: '/reports/sales',
    name: 'SalesReport',
    component: () => import('@/pages/SalesReport.vue'),
    meta: { roles: salonOps, feature: 'reports' },
  },
  {
    path: '/reports/reconciliation',
    name: 'Reconciliation',
    component: () => import('@/pages/Reconciliation.vue'),
    meta: { roles: salonOps, feature: ['reports', 'finance'] },
  },
  {
    // Any signed-in login: profile, security, notifications, workspaces, data.
    path: '/account',
    name: 'Account',
    component: () => import('@/pages/Account.vue'),
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/pages/Settings.vue'),
    meta: { roles: salonOps, feature: 'settings' },
  },
  {
    path: '/settings/payments',
    name: 'PaymentAccounts',
    component: () => import('@/pages/PaymentAccounts.vue'),
    meta: { roles: salonOps, feature: 'settings' },
  },
  {
    path: '/settings/platform-fee',
    name: 'PlatformFee',
    component: () => import('@/pages/PlatformFee.vue'),
    meta: { roles: ['Salon Owner'] },
  },
  {
    path: '/settings/listing',
    name: 'ListingSettings',
    component: () => import('@/pages/ListingSettings.vue'),
    meta: { roles: salonOps, feature: 'settings' },
  },
  {
    path: '/settings/price-sharing',
    name: 'PriceSharing',
    component: () => import('@/pages/PriceSharing.vue'),
    meta: { roles: salonOps, feature: 'settings' },
  },

  // ── Platform admin (Glamora Platform Admin only) ─────────────────────────
  {
    path: '/platform/reports',
    name: 'PlatformReports',
    component: () => import('@/pages/PlatformReports.vue'),
    meta: { roles: platformAdmin },
  },
  {
    path: '/platform/referral-flags',
    name: 'PlatformReferralFlags',
    component: () => import('@/pages/PlatformReferralFlags.vue'),
    meta: { roles: platformAdmin },
  },
  {
    path: '/platform/settings',
    name: 'PlatformSettings',
    component: () => import('@/pages/PlatformSettings.vue'),
    meta: { roles: platformAdmin },
  },

  // ── Staff workspace ───────────────────────────────────────────────────────
  {
    path: '/my/day',
    name: 'MyDay',
    component: () => import('@/pages/MyDay.vue'),
    meta: { roles: ['Salon Staff'] },
  },
  {
    path: '/my/appointments/:name',
    name: 'ServiceDelivery',
    component: () => import('@/pages/ServiceDelivery.vue'),
    meta: { roles: allStaff },
  },
  {
    path: '/my/commissions',
    name: 'MyCommissions',
    component: () => import('@/pages/MyCommissions.vue'),
    meta: { roles: ['Salon Staff'] },
  },
  {
    path: '/my/time-off',
    name: 'TimeOff',
    component: () => import('@/pages/TimeOff.vue'),
    meta: { roles: ['Salon Staff'] },
  },
  {
    path: '/my/profile',
    name: 'StaffProfile',
    component: () => import('@/pages/StaffProfile.vue'),
    meta: { roles: allStaff },
  },

  // ── Customer portal ───────────────────────────────────────────────────────
  {
    path: '/portal',
    name: 'PortalHome',
    component: () => import('@/pages/PortalHome.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/book',
    name: 'Book',
    component: () => import('@/pages/Book.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/appointments',
    name: 'MyAppointments',
    component: () => import('@/pages/MyAppointments.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/orders',
    name: 'PortalOrders',
    component: () => import('@/pages/PortalOrders.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/orders/:order',
    name: 'PortalOrder',
    component: () => import('@/pages/PortalOrder.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/receipts',
    name: 'MyReceipts',
    component: () => import('@/pages/MyReceipts.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/receipts/:name',
    name: 'PortalReceipt',
    component: () => import('@/pages/PortalReceipt.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/loyalty',
    name: 'Loyalty',
    component: () => import('@/pages/Loyalty.vue'),
    meta: { roles: customer },
  },
  {
    path: '/portal/profile',
    name: 'CustomerProfile',
    component: () => import('@/pages/CustomerProfile.vue'),
    meta: { roles: customer },
  },

  // ── Public marketplace (guests and signed-in users; no role redirect) ──
  {
    path: '/salons',
    name: 'SalonDirectory',
    component: () => import('@/pages/market/Directory.vue'),
    meta: { public: true, layout: 'public' },
  },
  {
    path: '/salons/compare',
    name: 'SalonCompare',
    component: () => import('@/pages/market/Compare.vue'),
    meta: { public: true, layout: 'public' },
  },
  {
    path: '/salons/:slug/shop',
    name: 'SalonShop',
    component: () => import('@/pages/SalonShop.vue'),
    meta: { public: true, layout: 'public' },
  },
  {
    path: '/salons/:slug',
    name: 'SalonPage',
    component: () => import('@/pages/market/SalonPage.vue'),
    meta: { public: true, layout: 'public' },
  },
  {
    path: '/list-your-salon',
    name: 'ListingPlans',
    component: () => import('@/pages/market/ListingPlans.vue'),
    meta: { public: true, layout: 'public' },
  },

  // ── Catch-all ─────────────────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFound.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // The window is the scroll container: Back restores the position, a new
  // page starts at the top, and a query-only change (filters, paging) stays put.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash }
    if (to.path === from.path) return false
    return { top: 0 }
  },
})

/**
 * Referral code from a customer booking link (`?ref=`). Kept here so a guest
 * who is sent to sign-in or signup before Book.vue mounts still has it.
 * Book.vue reads and clears the same key.
 */
export const REFERRAL_CODE_STORAGE_KEY = 'glamora_referral_code'

function queryValue(
  query: RouteLocationNormalized['query'],
  key: string,
): string {
  const raw = query[key]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value.trim() : ''
}

/** `ref` on this URL, or inside the login `redirect` path (`/portal/book?ref=`). */
function referralCodeFromRoute(to: RouteLocationNormalized): string {
  const direct = queryValue(to.query, 'ref')
  if (direct) return direct
  const redirect = queryValue(to.query, 'redirect')
  if (!redirect.includes('ref=')) return ''
  try {
    return (
      new URL(redirect, 'http://localhost').searchParams.get('ref') || ''
    ).trim()
  } catch {
    return ''
  }
}

export function storeReferralCode(code: string): void {
  const value = code.trim()
  if (!value) return
  try {
    sessionStorage.setItem(REFERRAL_CODE_STORAGE_KEY, value)
  } catch {
    /* Private mode. The in-memory query still works once Book.vue is open. */
  }
}

export function readStoredReferralCode(): string {
  try {
    return (sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY) || '').trim()
  } catch {
    return ''
  }
}

export function clearStoredReferralCode(): void {
  try {
    sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY)
  } catch {
    /* Already gone, or storage is blocked. */
  }
}

function rememberReferralFromRoute(to: RouteLocationNormalized): void {
  const code = referralCodeFromRoute(to)
  if (code) storeReferralCode(code)
}

/**
 * Where a navigation goes once the session is known: the role home for `/`,
 * Login for a signed-out visitor on a protected route, otherwise where it was
 * headed. An authenticated deep-link to a route the role cannot access is let
 * through: AppShell renders the "You don't have access" screen (with a Button
 * back to the role home) instead of the page component.
 */
function bootRedirect(to: RouteLocationNormalized): RouteLocationRaw | true {
  if (to.name === 'Home') {
    if (session.boot?.authenticated) {
      return { path: roleHomeRoute(session.boot) }
    }
    return { name: 'Login' }
  }

  // Login, signup, and the marketplace are public for guests and for a
  // signed-in user. Nothing here sends an authenticated visitor back home.
  if (to.meta.public) return true

  if (!session.boot?.authenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  return true
}

/**
 * Unsaved-work guard (`GLB-03`). `App.vue` wraps `<router-view>` in `AppShell`,
 * so `AppShell` is not a route component and an `onBeforeRouteLeave` registered
 * there is never collected. Registering on the router itself covers sidebar
 * navigation, in-app Back and forward/back history. A cancelled navigation
 * stays on the page and preserves the input.
 *
 * `approvedNavigation` lets a redirect chain (e.g. `/` → role home) finish
 * without asking twice; `afterEach` clears it on success, abort and error.
 */
let approvedNavigation = false

router.beforeEach(async () => {
  if (approvedNavigation) {
    approvedNavigation = false
    return true
  }
  if (!hasDirtyChanges()) return true
  const ok = await confirmDiscard()
  if (ok) approvedNavigation = true
  return ok
})

router.afterEach(() => {
  approvedNavigation = false
})

router.beforeEach(async (to) => {
  // Before the guest redirect: Book.vue does not mount until after sign-in.
  rememberReferralFromRoute(to)

  // Load session once
  if (!session.loaded && !session.loading) {
    try {
      await fetchBoot()
    } catch (e) {
      // Only a 401 / 403 means "not signed in" (fetchBoot already reset the
      // boot). A network error, timeout or 5xx says nothing about the session:
      // keep the route and let App.vue show "Can't reach the server" with a
      // Retry, instead of sending a signed-in user to Login.
      if (failureKind(e) !== 'auth') {
        session.unreachable = unreachableText(e)
        return true
      }
    }
  }

  // Still waiting for a Retry to reach the server: the route is kept as it is.
  if (session.unreachable) return true

  return bootRedirect(to)
})

/**
 * The Retry button of the "Can't reach the server" screen: check the session
 * again and, once the server answered, run the navigation rules that were
 * skipped for the current route.
 */
export async function retryBootAndRoute(): Promise<void> {
  await retryBoot()
  if (session.unreachable) return
  const destination = bootRedirect(router.currentRoute.value)
  if (destination !== true) await router.replace(destination)
}

export default router
